import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  X, Calendar, Clock, Users, Phone, User, Check,
  ChevronLeft, ChevronRight, Loader2, CalendarClock,
  Search, UserPlus, Star, UserCheck, Timer
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useCustomer } from '../../../hooks/useCustomer';
import reservationService from '../../../services/reservationService';

/**
 * QuickReserveModal - Intuitive table reservation with time slots
 *
 * Revamped Design:
 * - Teal color scheme (standard)
 * - Two-column layout: Date/Time (left) | Customer (right)
 * - Existing customer search with inline results
 * - New customer form option
 */
export default function QuickReserveModal({
  isOpen,
  onClose,
  table,
  onReserve
}) {
  // Customer hook
  const { customers, loading: loadingCustomers } = useCustomer();

  // Form State
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [guestCount, setGuestCount] = useState(2);
  const [expectedDurationMins, setExpectedDurationMins] = useState(90);
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Customer State
  const [customerMode, setCustomerMode] = useState('search'); // 'search' | 'new'
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [newCustomerName, setNewCustomerName] = useState('');
  const [newCustomerPhone, setNewCustomerPhone] = useState('');

  const searchInputRef = useRef(null);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedDate(new Date());
      setSelectedTimeSlot(null);
      setGuestCount(2);
      setExpectedDurationMins(90);
      setNotes('');
      setErrors({});
      setCustomerMode('search');
      setSearchQuery('');
      setSelectedCustomer(null);
      setNewCustomerName('');
      setNewCustomerPhone('');
    }
  }, [isOpen]);

  // Filter customers based on search
  const filteredCustomers = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const query = searchQuery.toLowerCase().trim();
    return customers
      .filter(c =>
        c.name?.toLowerCase().includes(query) ||
        c.phone?.includes(query)
      )
      .slice(0, 5);
  }, [searchQuery, customers]);

  // Generate time slots (11 AM to 11 PM in 30-min intervals)
  const timeSlots = useMemo(() => {
    const slots = [];
    const startHour = 11;
    const endHour = 23;

    for (let hour = startHour; hour < endHour; hour++) {
      for (let min = 0; min < 60; min += 30) {
        const time24 = `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
        const hour12 = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const timeDisplay = `${hour12}:${min.toString().padStart(2, '0')} ${ampm}`;

        slots.push({ value: time24, display: timeDisplay, hour, minute: min });
      }
    }
    return slots;
  }, []);

  // Check if a time slot is in the past (for today only)
  const isSlotPast = (slot) => {
    const now = new Date();
    const isToday = selectedDate.toDateString() === now.toDateString();
    if (!isToday) return false;
    const slotTime = new Date(selectedDate);
    slotTime.setHours(slot.hour, slot.minute, 0, 0);
    return slotTime <= now;
  };

  // Check if slot is already reserved for this table
const isSlotBlocked = (slot) => {
  if (!table?.reservations?.length) return false;

  const selectedDateStr = selectedDate.toISOString().split('T')[0];

  return table.reservations.some(
    (r) =>
      r.reservationDate === selectedDateStr &&
      r.reservationTime === slot.value &&
      !['cancelled', 'completed', 'no_show'].includes(r.status)
  );
};



  // Quick date options
  const quickDates = useMemo(() => {
    const today = new Date();
    const dates = [];
    for (let i = 0; i < 5; i++) {
      const d = new Date(today);
      d.setDate(d.getDate() + i);
      const label = i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : d.toLocaleDateString('en-IN', { weekday: 'short' });
      dates.push({ date: d, label, dateStr: d.getDate(), month: d.toLocaleDateString('en-IN', { month: 'short' }) });
    }
    return dates;
  }, []);

  // Group time slots by period (show all slots, disable past ones)
  const groupedSlots = useMemo(() => ({
    lunch: timeSlots.filter(s => s.hour >= 11 && s.hour < 15),
    evening: timeSlots.filter(s => s.hour >= 15 && s.hour < 19),
    dinner: timeSlots.filter(s => s.hour >= 19 && s.hour < 23),
  }), [timeSlots]);

  // Navigate date
  const navigateDate = (direction) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + direction);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (newDate >= today) {
      setSelectedDate(newDate);
      setSelectedTimeSlot(null);
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!selectedTimeSlot) {
      newErrors.timeSlot = 'Please select a time slot';
    }

    if (guestCount < 1) {
      newErrors.guestCount = 'At least 1 guest required';
    }

    // Customer validation
    if (customerMode === 'search' && !selectedCustomer) {
      newErrors.customer = 'Please select a customer or add new';
    }
    if (customerMode === 'new') {
      if (!newCustomerName.trim()) {
        newErrors.customerName = 'Name is required';
      }
      if (!newCustomerPhone.trim()) {
        newErrors.customerPhone = 'Phone is required';
      } else if (!/^\d{10}$/.test(newCustomerPhone.replace(/\D/g, ''))) {
        newErrors.customerPhone = 'Enter valid 10-digit phone';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Format duration for display (e.g., 90 -> "1h 30m", 120 -> "2h")
  const formatDuration = (mins) => {
    const hours = Math.floor(mins / 60);
    const minutes = mins % 60;
    if (hours === 0) return `${minutes}m`;
    if (minutes === 0) return `${hours}h`;
    return `${hours}h ${minutes}m`;
  };

  // Handle submit
  const handleSubmit = async () => {
    if (!validateForm()) return;

  if (selectedTimeSlot && isSlotBlocked(selectedTimeSlot)) {
    toast.error('This table is already reserved for this time');
    return;
  }

    setIsSubmitting(true);

    try {
      // Build reservation payload for API (matching DTO field names)
      const reservationPayload = {
        idTable: table.id,
        idCustomer: customerMode === 'search' ? selectedCustomer.id : null,
        customerName: customerMode === 'search' ? selectedCustomer.name : newCustomerName.trim(),
        customerPhone: customerMode === 'search' ? selectedCustomer.phone : newCustomerPhone.replace(/\D/g, ''),
        reservationDate: selectedDate.toISOString().split('T')[0],
        reservationTime: selectedTimeSlot.value,
        expectedDurationMins,
        guestCount,
        notes: notes.trim() || null,
        status: 'confirmed'
      };

      // Call the reservation API
      await reservationService.create(reservationPayload);

      toast.success(`Table ${table.tableNumber || table.name} reserved for ${selectedTimeSlot.display}`);

      // Notify parent to refresh tables
      if (onReserve) {
        onReserve();
      }

      onClose();

    } catch (error) {
      console.error('Reservation failed:', error);
      const errorMessage = error.response?.data?.message || 'Failed to create reservation';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Select existing customer
  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setSearchQuery('');
    setErrors(prev => ({ ...prev, customer: null }));
  };

  if (!isOpen) return null;
  const isSubmitDisabled =
  isSubmitting ||
  !selectedTimeSlot ||
  isSlotBlocked(selectedTimeSlot);


  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-teal-50">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-teal-500 rounded-xl flex items-center justify-center shadow-md">
              <CalendarClock size={24} className="text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-800">Reserve Table {table?.tableNumber || table?.name}</h2>
              <p className="text-sm text-slate-500">
                Capacity: {table?.seatingCapacity || table?.capacity} guests • {table?.section || 'Main'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white rounded-xl transition-all"
          >
            <X size={22} className="text-slate-500" />
          </button>
        </div>

        {/* Two Column Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 divide-y lg:divide-y-0 lg:divide-x divide-slate-200">

            {/* LEFT: Date & Time Section (3/5 width) */}
            <div className="lg:col-span-3 p-6 space-y-5">
              <div className="flex items-center gap-2 text-teal-700 font-bold text-sm">
                <Calendar size={16} />
                <span>Date & Time</span>
              </div>

              {/* Compact Date Selection with Navigator */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigateDate(-1)}
                  disabled={selectedDate.toDateString() === new Date().toDateString()}
                  className="p-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                >
                  <ChevronLeft size={16} className="text-slate-600" />
                </button>

                <div className="flex-1 flex gap-1.5">
                  {quickDates.map((item, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        setSelectedDate(item.date);
                        setSelectedTimeSlot(null);
                      }}
                      className={`flex-1 py-1.5 px-1 rounded-lg font-semibold text-center transition-all ${
                        selectedDate.toDateString() === item.date.toDateString()
                          ? 'bg-teal-500 text-white shadow-sm'
                          : 'bg-slate-100 text-slate-600 hover:bg-teal-50 hover:text-teal-700'
                      }`}
                    >
                      <div className="text-sm font-bold leading-tight">{item.dateStr}</div>
                      <div className="text-[9px] opacity-75 leading-tight">{item.label}</div>
                    </button>
                  ))}
                </div>

                <button
                  onClick={() => navigateDate(1)}
                  className="p-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 transition-all"
                >
                  <ChevronRight size={16} className="text-slate-600" />
                </button>
              </div>

              {/* Selected Date Display */}
              <div className="text-center py-1.5 bg-teal-50 rounded-lg">
                <p className="text-xs font-semibold text-teal-700">
                  {selectedDate.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
              </div>

              {/* Time Slots */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-medium text-slate-500 flex items-center gap-1">
                    <Clock size={12} /> Select Time
                  </p>
                  {errors.timeSlot && (
                    <span className="text-xs text-red-500">{errors.timeSlot}</span>
                  )}
                </div>

                <div className="space-y-4">
                  {/* Lunch */}
                  {groupedSlots.lunch.length > 0 && (
                    <div>
                      <p className="text-[10px] font-bold text-amber-600 mb-1 uppercase tracking-wide">☀️ Lunch (11AM - 3PM)</p>
                      <div className="grid grid-cols-8 gap-1.5">
                        {groupedSlots.lunch.map((slot) => {
                          const isPast = isSlotPast(slot);
                          const isBlocked = isSlotBlocked(slot); 
                          const isSelected = selectedTimeSlot?.value === slot.value;
                          return (
                            <button
                              key={slot.value}
                              onClick={() => !isPast && !isBlocked && setSelectedTimeSlot(slot)}
                              // disabled={isPast || isBlocked} 
                              className={`py-1.5 px-1 rounded-md text-[11px] font-semibold transition-all ${
                                isSelected
                                  ? 'bg-teal-500 text-white shadow-md ring-2 ring-teal-300'
                                  : isBlocked
                                    ? 'bg-red-100 text-red-400 cursor-not-allowed line-through' 
                                  : isPast
                                  ? 'bg-slate-100 text-slate-400 cursor-not-allowed line-through'
                                  : 'bg-amber-50 text-amber-800 hover:bg-amber-100'
                              }`}
                            >
                              <span
                                  title={isBlocked ? 'This time slot is already reserved' : ''}
                                  >
                                    {slot.display}
                              </span>

                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Evening */}
                  {groupedSlots.evening.length > 0 && (
                    <div>
                      <p className="text-[10px] font-bold text-orange-600 mb-1 uppercase tracking-wide">🌅 Evening (3PM - 7PM)</p>
                      <div className="grid grid-cols-8 gap-1.5">
                        {groupedSlots.evening.map((slot) => {
                          const isPast = isSlotPast(slot);
                          const isBlocked = isSlotBlocked(slot);
                          const isSelected = selectedTimeSlot?.value === slot.value;
                          return (
                            <button
                              key={slot.value}
                              onClick={() => !isPast && !isBlocked && setSelectedTimeSlot(slot)}
                              disabled={isPast || isBlocked} 
                              className={`py-1.5 px-1 rounded-md text-[11px] font-semibold transition-all ${
                                isSelected
                                  ? 'bg-teal-500 text-white shadow-md ring-2 ring-teal-300'
                                  : isBlocked
                                  ? 'bg-red-100 text-red-400 cursor-not-allowed line-through'
                                  : isPast
                                  ? 'bg-slate-100 text-slate-400 cursor-not-allowed line-through'
                                  : 'bg-orange-50 text-orange-800 hover:bg-orange-100'
                              }`}
                            >
                              <span
                                  title={isBlocked ? 'This time slot is already reserved' : ''}
                                  >
                                      {slot.display}
                              </span>

                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Dinner */}
                  {groupedSlots.dinner.length > 0 && (
                    <div>
                      <p className="text-[10px] font-bold text-indigo-600 mb-1 uppercase tracking-wide">🌙 Dinner (7PM - 11PM)</p>
                      <div className="grid grid-cols-8 gap-1.5">
                        {groupedSlots.dinner.map((slot) => {
                          const isPast = isSlotPast(slot);
                          const isBlocked = isSlotBlocked(slot); 
                          const isSelected = selectedTimeSlot?.value === slot.value;
                          return (
                            <button
                              key={slot.value}
                              onClick={() => !isPast && !isBlocked && setSelectedTimeSlot(slot)}
                              disabled={isPast || isBlocked} 
                              className={`py-1.5 px-1 rounded-md text-[11px] font-semibold transition-all ${
                                isSelected
                                  ? 'bg-teal-500 text-white shadow-md ring-2 ring-teal-300'
                                  : isBlocked
                                  ? 'bg-red-100 text-red-400 cursor-not-allowed line-through'
                                  : isPast
                                  ? 'bg-slate-100 text-slate-400 cursor-not-allowed line-through'
                                  : 'bg-indigo-50 text-indigo-800 hover:bg-indigo-100'
                              }`}
                            >
                              <span
                                   title={isBlocked ? 'This time slot is already reserved' : ''}
                              >
                                {slot.display}
                              </span>

                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Guest Count - Compact */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-xs font-medium text-slate-500 flex items-center gap-1">
                    <Users size={12} /> Guests
                  </p>
                  {errors.guestCount && (
                    <span className="text-xs text-red-500">{errors.guestCount}</span>
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  {[1, 2, 3, 4, 5, 6].map((num) => (
                    <button
                      key={num}
                      onClick={() => setGuestCount(num)}
                      className={`flex-1 py-2 rounded-lg font-bold text-sm transition-all ${
                        guestCount === num
                          ? 'bg-teal-500 text-white shadow-sm'
                          : 'bg-slate-100 text-slate-700 hover:bg-teal-50'
                      }`}
                    >
                      {num}
                    </button>
                  ))}
                  <input
                    type="number"
                    min="1"
                    value={guestCount}
                    onChange={(e) => setGuestCount(parseInt(e.target.value) || 1)}
                    className="w-12 py-2 text-center border-2 border-slate-200 rounded-lg font-bold text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none"
                  />
                </div>
              </div>

              {/* Expected Duration */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-xs font-medium text-slate-500 flex items-center gap-1">
                    <Timer size={12} /> Expected Duration
                  </p>
                </div>
                <div className="flex items-center gap-1.5">
                  {[60, 90, 120, 150, 180].map((mins) => (
                    <button
                      key={mins}
                      onClick={() => setExpectedDurationMins(mins)}
                      className={`flex-1 py-2 rounded-lg font-bold text-xs transition-all ${
                        expectedDurationMins === mins
                          ? 'bg-teal-500 text-white shadow-sm'
                          : 'bg-slate-100 text-slate-700 hover:bg-teal-50'
                      }`}
                    >
                      {formatDuration(mins)}
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-slate-400 mt-1">Table blocked 30 min before reservation</p>
              </div>
            </div>

            {/* RIGHT: Customer Section (2/5 width) */}
            <div className="lg:col-span-2 p-6 space-y-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-teal-700 font-bold text-sm">
                  <User size={16} />
                  <span>Customer Details</span>
                </div>
                {errors.customer && (
                  <span className="text-xs text-red-500">{errors.customer}</span>
                )}
              </div>

              {/* Customer Mode Toggle */}
              <div className="flex bg-slate-100 rounded-xl p-1">
                <button
                  onClick={() => {
                    setCustomerMode('search');
                    setErrors(prev => ({ ...prev, customerName: null, customerPhone: null }));
                  }}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
                    customerMode === 'search'
                      ? 'bg-white text-teal-700 shadow-sm'
                      : 'text-slate-600 hover:text-slate-800'
                  }`}
                >
                  <Search size={14} />
                  Existing Customer
                </button>
                <button
                  onClick={() => {
                    setCustomerMode('new');
                    setSelectedCustomer(null);
                    setErrors(prev => ({ ...prev, customer: null }));
                  }}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
                    customerMode === 'new'
                      ? 'bg-white text-teal-700 shadow-sm'
                      : 'text-slate-600 hover:text-slate-800'
                  }`}
                >
                  <UserPlus size={14} />
                  New Customer
                </button>
              </div>

              {/* Search Existing Customer */}
              {customerMode === 'search' && (
                <div className="space-y-3">
                  {/* Selected Customer Display */}
                  {selectedCustomer ? (
                    <div className="p-4 bg-teal-50 border-2 border-teal-200 rounded-xl">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-11 h-11 bg-teal-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                            {selectedCustomer.name?.charAt(0)?.toUpperCase()}
                          </div>
                          <div>
                            <p className="font-bold text-slate-800">{selectedCustomer.name}</p>
                            <div className="flex items-center gap-2 text-xs text-slate-500">
                              <Phone size={10} />
                              <span>{selectedCustomer.phone}</span>
                              {selectedCustomer.loyaltyPoints > 0 && (
                                <span className="flex items-center gap-0.5 text-amber-600 font-bold ml-2">
                                  <Star size={10} fill="currentColor" />
                                  {selectedCustomer.loyaltyPoints}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => setSelectedCustomer(null)}
                          className="px-3 py-1.5 bg-white hover:bg-slate-50 text-slate-600 text-xs font-semibold rounded-lg border border-slate-200 transition-all"
                        >
                          Change
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Search Input */}
                      <div className="relative">
                        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                          ref={searchInputRef}
                          type="text"
                          placeholder="Search by name or phone..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full pl-11 pr-4 py-3 border-2 border-slate-200 rounded-xl focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none text-sm"
                        />
                        {loadingCustomers && (
                          <Loader2 size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-teal-500 animate-spin" />
                        )}
                      </div>

                      {/* Search Results */}
                      {searchQuery && (
                        <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
                          {filteredCustomers.length > 0 ? (
                            filteredCustomers.map((customer) => (
                              <button
                                key={customer.id}
                                onClick={() => handleSelectCustomer(customer)}
                                className="w-full p-3 rounded-xl border-2 border-slate-200 hover:border-teal-300 hover:bg-teal-50 transition-all text-left flex items-center gap-3"
                              >
                                <div className="w-9 h-9 bg-slate-200 rounded-full flex items-center justify-center text-slate-600 font-bold">
                                  {customer.name?.charAt(0)?.toUpperCase()}
                                </div>
                                <div className="flex-1">
                                  <p className="font-semibold text-slate-800 text-sm">{customer.name}</p>
                                  <div className="flex items-center gap-2 text-xs text-slate-500">
                                    <span>{customer.phone}</span>
                                    {customer.loyaltyPoints > 0 && (
                                      <span className="text-amber-600 font-semibold">
                                        {customer.loyaltyPoints} pts
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <UserCheck size={18} className="text-teal-500" />
                              </button>
                            ))
                          ) : (
                            <div className="text-center py-4 bg-slate-50 rounded-xl">
                              <p className="text-sm text-slate-500">No customers found</p>
                              <button
                                onClick={() => {
                                  setCustomerMode('new');
                                  if (/^\d+$/.test(searchQuery)) {
                                    setNewCustomerPhone(searchQuery);
                                  } else {
                                    setNewCustomerName(searchQuery);
                                  }
                                }}
                                className="mt-2 text-xs text-teal-600 font-semibold hover:underline"
                              >
                                + Add as new customer
                              </button>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Empty State */}
                      {!searchQuery && (
                        <div className="text-center py-6 bg-slate-50 rounded-xl">
                          <Search size={28} className="mx-auto text-slate-300 mb-2" />
                          <p className="text-sm text-slate-500">Search for existing customer</p>
                          <p className="text-xs text-slate-400 mt-1">or switch to "New Customer"</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* New Customer Form */}
              {customerMode === 'new' && (
                <div className="space-y-3">
                  {/* Name */}
                  <div>
                    <div className="relative">
                      <User size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        type="text"
                        placeholder="Customer Name *"
                        value={newCustomerName}
                        onChange={(e) => {
                          setNewCustomerName(e.target.value);
                          setErrors(prev => ({ ...prev, customerName: null }));
                        }}
                        className={`w-full pl-11 pr-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-teal-100 outline-none text-sm ${
                          errors.customerName ? 'border-red-300 focus:border-red-500' : 'border-slate-200 focus:border-teal-500'
                        }`}
                      />
                    </div>
                    {errors.customerName && (
                      <p className="text-xs text-red-500 mt-1 ml-1">{errors.customerName}</p>
                    )}
                  </div>

                  {/* Phone */}
                  <div>
                    <div className="relative">
                      <Phone size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        type="tel"
                        placeholder="Phone Number *"
                        value={newCustomerPhone}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                          setNewCustomerPhone(value);
                          setErrors(prev => ({ ...prev, customerPhone: null }));
                        }}
                        className={`w-full pl-11 pr-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-teal-100 outline-none text-sm ${
                          errors.customerPhone ? 'border-red-300 focus:border-red-500' : 'border-slate-200 focus:border-teal-500'
                        }`}
                      />
                    </div>
                    {errors.customerPhone && (
                      <p className="text-xs text-red-500 mt-1 ml-1">{errors.customerPhone}</p>
                    )}
                  </div>

                  <p className="text-xs text-slate-400 bg-slate-50 p-2 rounded-lg">
                    New customer will be created when reservation is confirmed
                  </p>
                </div>
              )}

              {/* Notes */}
              <div>
                <p className="text-xs font-medium text-slate-500 mb-1.5">Special Requests (optional)</p>
                <textarea
                  placeholder="Birthday celebration, window seat preference, etc."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-teal-500 focus:ring-2 focus:ring-teal-100 outline-none text-sm resize-none"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t-2 border-slate-100 p-4 bg-slate-50">
          {/* Reservation Summary */}
          {selectedTimeSlot && (selectedCustomer || (customerMode === 'new' && newCustomerName)) && (
            <div className="mb-4 p-3 bg-teal-50 border border-teal-200 rounded-xl flex items-center gap-3">
              <div className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center">
                <Check size={20} className="text-white" />
              </div>
              <div className="flex-1">
                <p className="text-xs font-medium text-teal-600">Reservation Summary</p>
                <p className="text-sm font-bold text-slate-800">
                  {customerMode === 'search' ? selectedCustomer?.name : newCustomerName} • {guestCount} guest{guestCount > 1 ? 's' : ''} • {selectedDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} at {selectedTimeSlot.display}
                </p>
                <p className="text-xs text-teal-600 mt-0.5">
                  Duration: {formatDuration(expectedDurationMins)}
                </p>
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-white text-slate-600 rounded-xl font-semibold hover:bg-slate-100 transition-all border-2 border-slate-200"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitDisabled}
              className="flex-1 px-4 py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-bold transition-all disabled:bg-teal-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Reserving...
                </>
              ) : (
                <>
                  <CalendarClock size={18} />
                  Confirm Reservation
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
